import { Input } from "@babackapp/ui/components/input";
import { Switch } from "@babackapp/ui/components/switch";
import { Badge } from "@babackapp/ui/components/badge";
import {
  ActionParameterTypeTimeDelayModes,
  getActionTypeInfo,
} from "@/lib/override-rules";
import type { ShopOverrideRuleActionDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@babackapp/ui/components/select";
import { cn } from "@babackapp/ui/lib/utils";
import { FormMessage } from "@babackapp/ui/components/form";
import {
  DateRangePicker,
  DateTrigger,
} from "@babackapp/ui/components/date-range-picker";
import { useEffect } from "react";
import { endOfDay } from "date-fns";
import { DAY_MS } from "@/lib/utils/date.ts";

interface ActionSelectorProps {
  value: ShopOverrideRuleActionDto["parameters"];
  onChange: (value: ShopOverrideRuleActionDto["parameters"]) => void;
  disabled?: boolean;
  readonly?: boolean;
  actionValueOptions?: { label: string; key: string }[];
  actionType: ShopOverrideRuleActionDto["type"];
}

export function ActionSelector({
  value,
  onChange,
  disabled,
  readonly = false,
  actionValueOptions,
  actionType,
}: ActionSelectorProps) {
  const t = useTranslation();
  const actionInfo = getActionTypeInfo(actionType);

  const handleParameterChange = (newValue: unknown, field = "value") => {
    onChange({
      ...value,
      [field]: newValue,
    });
  };

  const timeDelayMode: ActionParameterTypeTimeDelayModes =
    (value?.mode as ActionParameterTypeTimeDelayModes) ?? "relativeDelay";

  useEffect(() => {
    handleParameterChange(
      timeDelayMode === "relativeDelay"
        ? 30 * DAY_MS
        : endOfDay(new Date()).getTime(),
    );
  }, [timeDelayMode]);

  const currentValue = value?.value;

  return (
    <div className="flex flex-wrap items-center gap-4 p-3 border rounded-lg bg-gray-50">
      <div className="flex-1">
        <Badge variant="secondary" className="mb-1">
          {t(
            `overrideRules.actionTypes.${actionInfo?.type || actionType}.label`,
          )}
        </Badge>
        <p className="text-sm text-muted-foreground">
          {t(
            `overrideRules.actionTypes.${actionInfo?.type || actionType}.description`,
          )}
        </p>
      </div>
      {actionInfo && (
        <div
          className={cn(
            "flex items-center gap-2",
            actionInfo.parameterType === "STRING" && "flex-1 w-full min-w-full",
          )}
        >
          {actionInfo.parameterType === "BOOLEAN" && (
            <div className="flex items-center gap-2">
              <Switch
                checked={Boolean(currentValue)}
                onCheckedChange={handleParameterChange}
                disabled={disabled || readonly}
              />
              <span className="text-sm font-medium">
                {Boolean(currentValue) ? t("misc.yes") : t("misc.no")}
              </span>
            </div>
          )}
          {actionInfo.parameterType === "NUMBER" && (
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={(currentValue as number) || 0}
                onChange={(e) => handleParameterChange(Number(e.target.value))}
                disabled={disabled || readonly}
                className="w-16 h-8 text-sm bg-white"
                min={0}
              />
              {actionInfo.parameterSuffix && (
                <span className="text-sm text-muted-foreground">
                  {t(
                    `overrideRules.actionTypes.${actionInfo?.type || actionType}.parameterSuffix`,
                  )}
                </span>
              )}
            </div>
          )}
          {actionInfo.parameterType === "TIME_DELAY" && (
            <div className="flex items-center gap-2 flex-wrap xs:flex-nowrap">
              <div className="flex items-center gap-2 min-w-56">
                <Switch
                  checked={timeDelayMode === "relativeDelay"}
                  onCheckedChange={(checked) =>
                    handleParameterChange(
                      checked ? "relativeDelay" : "fixedDate",
                      "mode",
                    )
                  }
                  disabled={disabled || readonly}
                />
                <span className="text-sm font-medium whitespace-nowrap">
                  {t(`overrideRules.actionTypeModes.${timeDelayMode}`)}
                </span>
              </div>
              {timeDelayMode === "relativeDelay" && (
                <div className="flex items-center gap-2 min-w-32">
                  <Input
                    type="number"
                    value={((currentValue as number) || 0) / DAY_MS}
                    onChange={(e) =>
                      handleParameterChange(Number(e.target.value) * DAY_MS)
                    }
                    disabled={disabled || readonly}
                    className="w-16 h-8 text-sm bg-white"
                    min={0}
                  />
                  {actionInfo.parameterSuffix && (
                    <span className="text-sm text-muted-foreground">
                      {t(
                        `overrideRules.actionTypes.${actionInfo?.type || actionType}.parameterSuffix`,
                      )}
                    </span>
                  )}
                </div>
              )}
              {timeDelayMode === "fixedDate" && (
                <DateRangePicker
                  className="h-8  min-w-32"
                  range={{
                    from: (currentValue as number) || Date.now(),
                    to: undefined,
                  }}
                  canSelectRange={false}
                  customButton={
                    <DateTrigger
                      className="min-w-32 bg-white"
                      value={currentValue as number}
                      placeholder={t(
                        "overrideRules.misc.selectAValuePlaceholder",
                      )}
                    />
                  }
                  onSelect={(date: { from?: number; to?: number }) =>
                    handleParameterChange(
                      endOfDay(new Date(date.from ?? 0)).getTime(),
                    )
                  }
                  numberOfMonths={1}
                  closeOnSelect={true}
                />
              )}
            </div>
          )}
          {actionInfo.parameterType === "STRING" &&
            (actionValueOptions ? (
              <div className="flex flex-1 items-center gap-2 w-full">
                <Select
                  value={currentValue as string}
                  onValueChange={handleParameterChange}
                  disabled={disabled || readonly}
                >
                  <SelectTrigger className={cn("w-32 h-8 flex-1 bg-white")}>
                    <SelectValue
                      placeholder={t(
                        "overrideRules.misc.selectAValuePlaceholder",
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {actionValueOptions.map((option) => (
                      <SelectItem key={option.key} value={option.key}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Input
                  type="text"
                  value={currentValue as string}
                  onChange={(e) => handleParameterChange(e.target.value)}
                  disabled={disabled || readonly}
                  className="w-16 h-8 text-sm bg-white"
                />
              </div>
            ))}
        </div>
      )}
      <FormMessage />
    </div>
  );
}
